// src/components/SolicitudMudanzaModal.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import {
    Timestamp,
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    query,
    setDoc,
    serverTimestamp,
    where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import PropTypes from 'prop-types';
import { db, storage } from '../firebase';
import { registerMovement } from '../utils/registerMovement';
import './SolicitudMudanzaModal.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const DEFAULT_CONFIG = {
    diasPermitidos: ['mon', 'tue', 'wed', 'thu', 'fri'],
    horaInicio: '08:00',
    horaFin: '18:00',
    intervaloMinutos: 60,
    nota: '',
};

const DAY_OPTIONS = [
    { key: 'mon', label: 'Lunes', weekday: 1 },
    { key: 'tue', label: 'Martes', weekday: 2 },
    { key: 'wed', label: 'Miércoles', weekday: 3 },
    { key: 'thu', label: 'Jueves', weekday: 4 },
    { key: 'fri', label: 'Viernes', weekday: 5 },
    { key: 'sat', label: 'Sábado', weekday: 6 },
    { key: 'sun', label: 'Domingo', weekday: 0 },
];

const DAY_KEY_BY_INDEX = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const STEP_TITLES = [
    'Datos personales',
    'Programación',
    'Documentos requeridos',
    'Confirmar solicitud',
];

const pickFirstString = (values = []) => {
    for (const raw of values) {
        if (raw == null) {
            continue;
        }
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.length > 0) {
                return trimmed;
            }
        } else if (typeof raw === 'number') {
            const converted = String(raw);
            if (converted.trim().length > 0) {
                return converted.trim();
            }
        }
    }
    return '';
};

const buildCasaCandidates = (userData) => {
    if (!userData || typeof userData !== 'object') {
        return [];
    }
    const rawCandidates = [
        userData.numeroCasa,
        userData.numeroLote,
        userData.numero,
        userData.lote,
        userData.houseLabel,
        userData.houseNumber,
        userData.loteNumero,
        userData.loteId,
        userData.casaNumero,
        userData.casaId,
        userData.identificadorCasa,
        userData.houseId,
        userData.casaCodigo,
        userData?.casa?.numero,
        userData?.casaAsignada?.numero,
        userData?.direccion?.numero,
    ];
    const seen = new Set();
    const result = [];
    rawCandidates.forEach((raw) => {
        if (raw == null) {
            return;
        }
        const candidate = typeof raw === 'number' ? String(raw) : String(raw || '').trim();
        if (candidate && !seen.has(candidate)) {
            seen.add(candidate);
            result.push(candidate);
        }
        if (candidate) {
            const compact = candidate.replace(/\s+/g, '');
            if (compact && !seen.has(compact)) {
                seen.add(compact);
                result.push(compact);
            }
            const match = candidate.match(/(\d{1,5}[\w-]*)/);
            if (match && match[1]) {
                const extracted = match[1];
                if (!seen.has(extracted)) {
                    seen.add(extracted);
                    result.push(extracted);
                }
            }
        }
    });
    return result;
};

const formatDayList = (days) => {
    if (!days.length) {
        return '';
    }
    if (days.length === 1) {
        return days[0];
    }
    const last = days[days.length - 1];
    const initial = days.slice(0, -1);
    return `${initial.join(', ')} y ${last}`;
};

const generateTimeSlots = (start, end, intervalMinutes) => {
    const slots = [];
    if (!start || !end) {
        return slots;
    }
    const interval = Number(intervalMinutes) > 0 ? Number(intervalMinutes) : 60;
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) {
        return slots;
    }
    const base = new Date();
    base.setSeconds(0, 0);
    base.setMilliseconds(0);
    const current = new Date(base);
    current.setHours(startHour, startMinute, 0, 0);
    const limit = new Date(base);
    limit.setHours(endHour, endMinute, 0, 0);
    let guard = 0;
    while (current <= limit && guard < 2880) { // protección contra bucles
        const hours = `${current.getHours()}`.padStart(2, '0');
        const minutes = `${current.getMinutes()}`.padStart(2, '0');
        slots.push(`${hours}:${minutes}`);
        current.setMinutes(current.getMinutes() + interval);
        guard += 1;
    }
    return slots;
};

const sanitizeFileName = (name) => name
    .normalize('NFD')
    .replace(/[^\w.-]/g, '_')
    .replace(/_{2,}/g, '_');

const uploadSolicitudFile = async (fraccionamientoId, uid, solicitudId, prefix, file) => {
    const safeName = sanitizeFileName(file.name || `${prefix}-${Date.now()}`);
    const storagePath = `fraccionamientos/${fraccionamientoId}/solicitudes/${uid}/${solicitudId}/${prefix}-${Date.now()}-${safeName}`;
    const storageRef = ref(storage, storagePath);
    const metadata = file.type ? { contentType: file.type } : undefined;
    await uploadBytes(storageRef, file, metadata);
    const url = await getDownloadURL(storageRef);
    return {
        url,
        name: file.name,
        size: file.size,
        type: metadata?.contentType || file.type || 'application/octet-stream',
    };
};

const SolicitudMudanzaModal = ({
    fraccionamientoId = null,
    currentUser = null,
    userData = null,
    onClose,
    onSuccess = null,
}) => {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(() => ({
        nombreCompleto: '',
        lote: '',
        telefonoPrincipal: '',
        telefonoSecundario: '',
        email: '',
        tipoMudanza: 'entrada',
        fechaProgramada: '',
        horario: '',
        empresaMudanza: '',
        placasVehiculos: '',
        observaciones: '',
        aceptaReglamento: false,
    }));
    const [files, setFiles] = useState({
        identificacion: null,
        comprobante: null,
        inventario: null,
    });
    const [residentCasaData, setResidentCasaData] = useState(null);
    const [loadingCasaData, setLoadingCasaData] = useState(false);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const autoDefaultsRef = useRef({
        nombreCompleto: '',
        telefonoPrincipal: '',
        telefonoSecundario: '',
        email: '',
        lote: '',
    });

    const casaCandidates = useMemo(() => buildCasaCandidates(userData), [userData]);
    const uidCandidates = useMemo(() => [
        currentUser?.uid,
        userData?.uid,
        userData?.userId,
        userData?.usuarioId,
    ].filter((value) => typeof value === 'string' && value.trim().length > 0), [currentUser?.uid, userData?.uid, userData?.userId, userData?.usuarioId]);

    useEffect(() => {
        const shouldSkipCasaLookup = !fraccionamientoId
            || (((!Array.isArray(casaCandidates) || casaCandidates.length === 0)
                && uidCandidates.length === 0));

        if (shouldSkipCasaLookup) {
            setResidentCasaData(null);
            setLoadingCasaData(false);
            return undefined;
        }

        let cancelled = false;
        const fetchCasaData = async () => {
            setLoadingCasaData(true);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                
                const tryAssignCasaFromDocRef = async (docRef) => {
                    try {
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            if (!cancelled) {
                                setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                            }
                            return true;
                        }
                    } catch (err) {
                        // Ignorar errores de doc no encontrado
                    }
                    return false;
                };

                // Primero intentar buscar por número de casa
                for (const candidate of casaCandidates) {
                    const normalized = typeof candidate === 'string' ? candidate.trim() : String(candidate || '').trim();
                    if (!normalized) {
                        continue;
                    }
                    try {
                        // Intentar buscar directamente por docId
                        const directDocRef = doc(casasRef, normalized);
                        const directMatch = await tryAssignCasaFromDocRef(directDocRef);
                        if (directMatch) {
                            return;
                        }

                        // Buscar por campo 'numero'
                        const snapshot = await getDocs(query(casasRef, where('numero', '==', normalized), limit(1)));
                        if (!snapshot.empty) {
                            if (!cancelled) {
                                const docSnap = snapshot.docs[0];
                                setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                            }
                            return;
                        }
                    } catch (innerError) {
                        console.error('SolicitudMudanzaModal: error buscando casa por número', normalized, innerError);
                    }
                }

                // Si no se encontró por número, buscar por UID
                const uidFields = ['uid', 'userId', 'usuarioId', 'residenteUid', 'residenteId', 'propietarioUid', 'propietarioId'];
                for (const field of uidFields) {
                    for (const uid of uidCandidates) {
                        const normalizedUid = uid.trim();
                        try {
                            const snapshot = await getDocs(query(casasRef, where(field, '==', normalizedUid), limit(1)));
                            if (!snapshot.empty) {
                                if (!cancelled) {
                                    const docSnap = snapshot.docs[0];
                                    setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                                }
                                return;
                            }
                        } catch (innerError) {
                            console.error(`SolicitudMudanzaModal: error buscando casa por ${field}`, normalizedUid, innerError);
                        }
                    }
                }

                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } catch (fetchError) {
                console.error('SolicitudMudanzaModal: no se pudo obtener información de la casa', fetchError);
                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingCasaData(false);
                }
            }
        };

        fetchCasaData();

        return () => {
            cancelled = true;
        };
    }, [casaCandidates, fraccionamientoId, uidCandidates]);

    const uploaderRole = useMemo(() => {
        if (typeof userData?.rol === 'string') {
            const normalized = userData.rol.trim().toLowerCase();
            if (normalized) {
                return normalized;
            }
        }
        return 'residente';
    }, [userData?.rol]);

    const defaultNombre = useMemo(() => pickFirstString([
        residentCasaData?.nombre_residente,
        residentCasaData?.nombre,
        residentCasaData?.titular,
        userData?.nombreCompleto,
        `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
        userData?.displayName,
    ]), [residentCasaData, userData]);

    // Mantener uploaderName para compatibilidad con código existente
    const uploaderName = useMemo(() => defaultNombre || '', [defaultNombre]);

    const defaultTelefono = useMemo(() => pickFirstString([
        residentCasaData?.telefono,
        residentCasaData?.telefono_principal,
        userData?.telefono,
        userData?.telefonoPrincipal,
        userData?.phoneNumber,
    ]), [residentCasaData, userData]);

    const defaultTelefonoSecundario = useMemo(() => pickFirstString([
        residentCasaData?.telefono2,
        residentCasaData?.telefonoSecundario,
        userData?.telefonoSecundario,
        userData?.telefonoAlterno,
    ]), [residentCasaData, userData]);

    const defaultEmail = useMemo(() => pickFirstString([
        residentCasaData?.email,
        userData?.correo,
        userData?.email,
        userData?.emailPrincipal,
    ]), [residentCasaData, userData]);

    const defaultLote = useMemo(() => pickFirstString([
        residentCasaData?.numero,
        residentCasaData?.numeroCasa,
        residentCasaData?.lote,
        userData?.numeroCasa,
        userData?.numeroLote,
        userData?.lote,
        userData?.houseLabel,
    ]), [residentCasaData, userData]);

    useEffect(() => {
        console.log('SolicitudMudanzaModal - residentCasaData:', residentCasaData);
        console.log('SolicitudMudanzaModal - valores calculados:', {
            defaultNombre,
            defaultTelefono,
            defaultEmail,
            defaultLote,
        });
        
        setForm((prev) => ({
            ...prev,
            nombreCompleto: defaultNombre || prev.nombreCompleto,
            telefonoPrincipal: defaultTelefono || prev.telefonoPrincipal,
            telefonoSecundario: defaultTelefonoSecundario || prev.telefonoSecundario,
            email: (defaultEmail || prev.email).toLowerCase(),
            lote: defaultLote || prev.lote,
        }));
    }, [residentCasaData, defaultNombre, defaultTelefono, defaultTelefonoSecundario, defaultEmail, defaultLote]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setConfig(DEFAULT_CONFIG);
            setLoadingConfig(false);
            return undefined;
        }
        setLoadingConfig(true);
        const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'mudanzas');
        const unsubscribe = onSnapshot(
            configRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data() || {};
                    setConfig({
                        diasPermitidos: Array.isArray(data.diasPermitidos) && data.diasPermitidos.length
                            ? data.diasPermitidos.map((item) => String(item).toLowerCase())
                            : DEFAULT_CONFIG.diasPermitidos,
                        horaInicio: typeof data.horaInicio === 'string' && data.horaInicio.length === 5
                            ? data.horaInicio
                            : DEFAULT_CONFIG.horaInicio,
                        horaFin: typeof data.horaFin === 'string' && data.horaFin.length === 5
                            ? data.horaFin
                            : DEFAULT_CONFIG.horaFin,
                        intervaloMinutos: Number.isInteger(data.intervaloMinutos)
                            ? data.intervaloMinutos
                            : DEFAULT_CONFIG.intervaloMinutos,
                        nota: typeof data.nota === 'string' ? data.nota : DEFAULT_CONFIG.nota,
                    });
                } else {
                    setConfig(DEFAULT_CONFIG);
                }
                setLoadingConfig(false);
            },
            (err) => {
                console.error('SolicitudMudanzaModal: no se pudo cargar la configuración de mudanzas', err);
                setConfig(DEFAULT_CONFIG);
                setLoadingConfig(false);
            },
        );
        return unsubscribe;
    }, [fraccionamientoId]);

    const allowedDaysSet = useMemo(() => {
        const list = Array.isArray(config.diasPermitidos) && config.diasPermitidos.length
            ? config.diasPermitidos
            : DEFAULT_CONFIG.diasPermitidos;
        return new Set(list.map((item) => String(item).toLowerCase()));
    }, [config.diasPermitidos]);

    const timeSlots = useMemo(() => generateTimeSlots(
        config.horaInicio,
        config.horaFin,
        config.intervaloMinutos,
    ), [config.horaFin, config.horaInicio, config.intervaloMinutos]);

    useEffect(() => {
        if (form.horario && !timeSlots.includes(form.horario)) {
            setForm((prev) => ({ ...prev, horario: '' }));
        }
    }, [form.horario, timeSlots]);

    const minDate = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today.toISOString().split('T')[0];
    }, []);

    const scheduleDescription = useMemo(() => {
        const allowedLabels = DAY_OPTIONS
            .filter((day) => allowedDaysSet.has(day.key))
            .map((day) => day.label);
        const fallback = `Mudanzas disponibles ${allowedLabels.length ? `los ${formatDayList(allowedLabels)}` : 'en días permitidos por la administración'} de ${config.horaInicio} a ${config.horaFin} hrs.`;
        const trimmed = (config.nota || '').trim();
        return trimmed.length ? trimmed : fallback;
    }, [allowedDaysSet, config.horaFin, config.horaInicio, config.nota]);

    const isDayAllowed = useCallback((dateString) => {
        if (!dateString) {
            return false;
        }
        const date = new Date(`${dateString}T00:00:00`);
        if (Number.isNaN(date.getTime())) {
            return false;
        }
        const key = DAY_KEY_BY_INDEX[date.getDay()];
        return allowedDaysSet.has(key);
    }, [allowedDaysSet]);

    const handleInputChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        setError('');
    }, []);

    const handleFileChange = useCallback((field) => (event) => {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        if (!file) {
            setFiles((prev) => ({ ...prev, [field]: null }));
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError('Cada archivo debe pesar máximo 10 MB.');
            return;
        }
        const mime = file.type || '';
        const normalized = file.name.toLowerCase();
        const isAccepted = ACCEPTED_TYPES.includes(mime)
            || normalized.endsWith('.pdf')
            || normalized.endsWith('.png')
            || normalized.endsWith('.jpg')
            || normalized.endsWith('.jpeg');
        if (!isAccepted) {
            setError('Los archivos deben ser PDF, JPG o PNG.');
            return;
        }
        setError('');
        setFiles((prev) => ({ ...prev, [field]: file }));
    }, []);

    const handleRemoveFile = useCallback((field) => {
        setFiles((prev) => ({ ...prev, [field]: null }));
    }, []);

    const validateStep = useCallback((currentStep) => {
        switch (currentStep) {
            case 0:
                if (!form.nombreCompleto.trim()) {
                    setError('Indica el nombre completo del solicitante.');
                    return false;
                }
                if (!form.lote.trim()) {
                    setError('Ingresa el número de lote o casa.');
                    return false;
                }
                if (!form.telefonoPrincipal.trim()) {
                    setError('Captura al menos un teléfono de contacto.');
                    return false;
                }
                if (!form.email.trim()) {
                    setError('Ingresa un correo electrónico.');
                    return false;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
                    setError('El correo electrónico no tiene un formato válido.');
                    return false;
                }
                setError('');
                return true;
            case 1:
                if (!form.fechaProgramada) {
                    setError('Selecciona la fecha programada de la mudanza.');
                    return false;
                }
                if (!isDayAllowed(form.fechaProgramada)) {
                    setError('La fecha elegida no está dentro de los días permitidos.');
                    return false;
                }
                if (!form.horario) {
                    setError('Selecciona un horario disponible.');
                    return false;
                }
                setError('');
                return true;
            case 2:
                if (!files.identificacion) {
                    setError('Adjunta la identificación oficial.');
                    return false;
                }
                if (!files.comprobante) {
                    setError('Adjunta el comprobante de domicilio.');
                    return false;
                }
                setError('');
                return true;
            case 3:
                if (!form.aceptaReglamento) {
                    setError('Debes aceptar el reglamento de mudanzas para continuar.');
                    return false;
                }
                setError('');
                return true;
            default:
                return true;
        }
    }, [files.comprobante, files.identificacion, form, isDayAllowed]);

    const handleNext = useCallback(() => {
        if (validateStep(step)) {
            setStep((prev) => Math.min(prev + 1, STEP_TITLES.length - 1));
        }
    }, [step, validateStep]);

    const handlePrev = useCallback(() => {
        setError('');
        setStep((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!validateStep(3)) {
            return;
        }
        if (!fraccionamientoId || !currentUser?.uid) {
            setError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }
        try {
            setSaving(true);
            setError('');
            const programDate = new Date(`${form.fechaProgramada}T${form.horario}:00`);
            if (Number.isNaN(programDate.getTime())) {
                throw new Error('Fecha programada inválida');
            }
            const programTimestamp = Timestamp.fromDate(programDate);
            const documentosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
            const solicitudRef = doc(documentosRef);

            const [identInfo, comprobanteInfo, inventarioInfo] = await Promise.all([
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'identificacion', files.identificacion),
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'comprobante', files.comprobante),
                files.inventario
                    ? uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'inventario', files.inventario)
                    : Promise.resolve(null),
            ]);

            const payload = {
                segmento: 'solicitudes',
                titulo: 'Permiso de Mudanza',
                descripcion: `Solicitud de mudanza para ${form.lote || 'lote sin especificar'}`,
                tipoSolicitud: 'mudanza',
                fraccionamientoId,
                createdAt: serverTimestamp(),
                uploadedBy: currentUser.uid,
                uploaderNombre: uploaderName || form.nombreCompleto.trim(),
                uploaderRol: uploaderRole,
                estado: 'pendiente',
                tipoMudanza: form.tipoMudanza,
                nombreCompleto: form.nombreCompleto.trim(),
                lote: form.lote.trim(),
                telefonoPrincipal: form.telefonoPrincipal.trim(),
                email: form.email.trim().toLowerCase(),
                fechaProgramada: programTimestamp,
                horaProgramada: form.horario,
                documentosAdjuntos: inventarioInfo ? 3 : 2,
                docIdentificacionUrl: identInfo.url,
                docIdentificacionName: identInfo.name,
                docIdentificacionSize: identInfo.size,
                docIdentificacionType: identInfo.type,
                docComprobanteUrl: comprobanteInfo.url,
                docComprobanteName: comprobanteInfo.name,
                docComprobanteSize: comprobanteInfo.size,
                docComprobanteType: comprobanteInfo.type,
            };

            if (form.telefonoSecundario.trim()) {
                payload.telefonoSecundario = form.telefonoSecundario.trim();
            }
            if (form.empresaMudanza.trim()) {
                payload.empresaMudanza = form.empresaMudanza.trim();
            }
            if (form.placasVehiculos.trim()) {
                payload.placasVehiculos = form.placasVehiculos.trim();
            }
            if (form.observaciones.trim()) {
                payload.observaciones = form.observaciones.trim();
            }
            if (inventarioInfo) {
                payload.docInventarioUrl = inventarioInfo.url;
                payload.docInventarioName = inventarioInfo.name;
                payload.docInventarioSize = inventarioInfo.size;
                payload.docInventarioType = inventarioInfo.type;
            }

            await setDoc(solicitudRef, payload);

            try {
                const fechaLabel = programDate.toLocaleDateString('es-MX');
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Solicitud de mudanza registrada - ${payload.uploaderNombre || 'Residente'}`,
                    descripcion: `Tipo ${form.tipoMudanza || 'programada'} · ${fechaLabel}${form.horario ? ` · ${form.horario} hrs` : ''}`,
                    categoria: 'Solicitudes y Permisos',
                    tipoMovimiento: 'solicitud_creada',
                    origen: 'solicitudes',
                    metadata: {
                        solicitudId: solicitudRef.id,
                        tipoSolicitud: 'mudanza',
                        estado: 'pendiente',
                        fechaProgramadaIso: programDate.toISOString(),
                        horaProgramada: form.horario || null,
                        tipoMudanza: form.tipoMudanza,
                        lote: form.lote.trim() || null,
                    },
                });
            } catch (logError) {
                console.error('SolicitudMudanzaModal: no se pudo registrar el movimiento de creación', logError);
            }

            if (onSuccess) {
                onSuccess('Solicitud de mudanza enviada. Te avisaremos cuando la administración la revise.');
            }
        } catch (err) {
            console.error('SolicitudMudanzaModal: error al registrar la solicitud de mudanza', err);
            setError('No pudimos registrar la solicitud. Intenta nuevamente.');
            setSaving(false);
            return;
        }
        setSaving(false);
    }, [currentUser, files.comprobante, files.identificacion, files.inventario, form, fraccionamientoId, onSuccess, uploaderName, uploaderRole, validateStep]);

    return (
        <div className="mudanza-modal-overlay" role="presentation">
            <div className="mudanza-modal" role="dialog" aria-modal="true" aria-labelledby="mudanzaModalTitle">
                <header className="mudanza-modal-header">
                    <div className="mudanza-header-meta">
                        <span className="mudanza-template-badge">Permiso de Mudanza</span>
                        <h2 id="mudanzaModalTitle">Nueva solicitud</h2>
                    </div>
                    <button type="button" className="mudanza-close" onClick={onClose} aria-label="Cerrar">
                        <FaTimes />
                    </button>
                </header>

                <nav className="mudanza-steps" aria-label="Progreso de la solicitud">
                    {STEP_TITLES.map((title, index) => {
                        const status = index === step ? 'active' : (index < step ? 'complete' : 'pending');
                        return (
                            <div key={title} className={`mudanza-step ${status}`}>
                                <span className="mudanza-step-index">{index + 1}</span>
                                <span className="mudanza-step-title">{title}</span>
                            </div>
                        );
                    })}
                </nav>

                {loadingConfig && (
                    <div className="mudanza-info">Cargando configuraciones de mudanzas...</div>
                )}

                {error && (
                    <div className="mudanza-error" role="alert">{error}</div>
                )}

                <div className="mudanza-body">
                    {loadingCasaData && step === 0 && (
                        <div className="mudanza-info">Cargando información registrada de tu vivienda…</div>
                    )}
                    {step === 0 && (
                        <div className="mudanza-section">
                            <h3>Datos personales</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Nombre completo *</span>
                                    <input
                                        type="text"
                                        name="nombreCompleto"
                                        value={form.nombreCompleto}
                                        onChange={handleInputChange}
                                        placeholder="Nombre del solicitante"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Número de lote o casa *</span>
                                    <input
                                        type="text"
                                        name="lote"
                                        value={form.lote}
                                        onChange={handleInputChange}
                                        placeholder="Ej. Lote 24"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Teléfono principal *</span>
                                    <input
                                        type="tel"
                                        name="telefonoPrincipal"
                                        value={form.telefonoPrincipal}
                                        onChange={handleInputChange}
                                        placeholder="10 dígitos"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Teléfono alterno</span>
                                    <input
                                        type="tel"
                                        name="telefonoSecundario"
                                        value={form.telefonoSecundario}
                                        onChange={handleInputChange}
                                        placeholder="Opcional"
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Correo electrónico *</span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleInputChange}
                                        placeholder="nombre@dominio.com"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Tipo de mudanza *</span>
                                    <select name="tipoMudanza" value={form.tipoMudanza} onChange={handleInputChange}>
                                        <option value="entrada">Entrada</option>
                                        <option value="salida">Salida</option>
                                        <option value="parcial">Parcial</option>
                                    </select>
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="mudanza-section">
                            <h3>Programación</h3>
                            <div className="mudanza-note" role="status">{scheduleDescription}</div>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Fecha programada *</span>
                                    <input
                                        type="date"
                                        name="fechaProgramada"
                                        value={form.fechaProgramada}
                                        min={minDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    {form.fechaProgramada && !isDayAllowed(form.fechaProgramada) && (
                                        <small className="mudanza-warning">La fecha seleccionada no coincide con los días permitidos.</small>
                                    )}
                                </label>
                                <label className="mudanza-field">
                                    <span>Horario *</span>
                                    <select
                                        name="horario"
                                        value={form.horario}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Selecciona un horario</option>
                                        {timeSlots.map((slot) => (
                                            <option key={slot} value={slot}>{slot} hrs</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field">
                                    <span>Empresa de mudanza</span>
                                    <input
                                        type="text"
                                    name="empresaMudanza"
                                        value={form.empresaMudanza}
                                        onChange={handleInputChange}
                                        placeholder="Opcional"
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Placas del vehículo</span>
                                    <input
                                        type="text"
                                        name="placasVehiculos"
                                        value={form.placasVehiculos}
                                        onChange={handleInputChange}
                                        placeholder="Ej. ABC-123"
                                    />
                                </label>
                            </div>
                            <label className="mudanza-field">
                                <span>Observaciones</span>
                                <textarea
                                    name="observaciones"
                                    value={form.observaciones}
                                    onChange={handleInputChange}
                                    rows={3}
                                    placeholder="Detalles relevantes para la administración o vigilancia"
                                />
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mudanza-section">
                            <h3>Documentos requeridos</h3>
                            <p className="mudanza-hint">Los formatos permitidos son PDF, JPG o PNG con un tamaño máximo de 10 MB por archivo.</p>
                            <div className="mudanza-upload-grid">
                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Identificación oficial (INE) *</h4>
                                        {files.identificacion && (
                                            <button type="button" onClick={() => handleRemoveFile('identificacion')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={handleFileChange('identificacion')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.identificacion ? files.identificacion.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG o PNG (máx. 10 MB)</small>
                                    </label>
                                </div>
                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Comprobante de domicilio *</h4>
                                        {files.comprobante && (
                                            <button type="button" onClick={() => handleRemoveFile('comprobante')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={handleFileChange('comprobante')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.comprobante ? files.comprobante.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG o PNG (máx. 10 MB)</small>
                                    </label>
                                </div>
                                <div className="mudanza-upload-card optional">
                                    <header>
                                        <h4>Inventario (opcional)</h4>
                                        {files.inventario && (
                                            <button type="button" onClick={() => handleRemoveFile('inventario')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={handleFileChange('inventario')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.inventario ? files.inventario.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG o PNG (máx. 10 MB)</small>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="mudanza-section">
                            <h3>Confirmar solicitud</h3>
                            <div className="mudanza-summary">
                                <div className="mudanza-summary-block">
                                    <h4>Datos del solicitante</h4>
                                    <ul>
                                        <li><strong>Nombre:</strong> {form.nombreCompleto || '—'}</li>
                                        <li><strong>Lote:</strong> {form.lote || '—'}</li>
                                        <li><strong>Teléfono:</strong> {form.telefonoPrincipal || '—'}</li>
                                        {form.telefonoSecundario && <li><strong>Teléfono alterno:</strong> {form.telefonoSecundario}</li>}
                                        <li><strong>Correo:</strong> {form.email || '—'}</li>
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Programación</h4>
                                    <ul>
                                        <li><strong>Tipo:</strong> {form.tipoMudanza ? form.tipoMudanza.charAt(0).toUpperCase() + form.tipoMudanza.slice(1) : '—'}</li>
                                        <li><strong>Fecha:</strong> {form.fechaProgramada || '—'}</li>
                                        <li><strong>Horario:</strong> {form.horario ? `${form.horario} hrs` : '—'}</li>
                                        {form.empresaMudanza && <li><strong>Empresa:</strong> {form.empresaMudanza}</li>}
                                        {form.placasVehiculos && <li><strong>Placas:</strong> {form.placasVehiculos}</li>}
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Documentos</h4>
                                    <ul>
                                        <li><FaCheckCircle /> Identificación oficial</li>
                                        <li><FaCheckCircle /> Comprobante de domicilio</li>
                                        <li><FaCheckCircle color={files.inventario ? '#2563eb' : '#94a3b8'} /> Inventario {files.inventario ? '(adjunto)' : '(opcional)'}</li>
                                    </ul>
                                </div>
                            </div>
                            {form.observaciones && (
                                <div className="mudanza-summary-block">
                                    <h4>Observaciones</h4>
                                    <p>{form.observaciones}</p>
                                </div>
                            )}
                            <label className="mudanza-terms">
                                <input
                                    type="checkbox"
                                    name="aceptaReglamento"
                                    checked={form.aceptaReglamento}
                                    onChange={handleInputChange}
                                />
                                <span>Acepto el reglamento de mudanzas del fraccionamiento.</span>
                            </label>
                        </div>
                    )}
                </div>

                <footer className="mudanza-footer">
                    <button type="button" className="mudanza-btn ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <div className="mudanza-footer-actions">
                        {step > 0 && (
                            <button type="button" className="mudanza-btn secondary" onClick={handlePrev} disabled={saving}>
                                <FaArrowLeft /> Anterior
                            </button>
                        )}
                        {step < STEP_TITLES.length - 1 && (
                            <button type="button" className="mudanza-btn primary" onClick={handleNext} disabled={saving}>
                                Siguiente <FaArrowRight />
                            </button>
                        )}
                        {step === STEP_TITLES.length - 1 && (
                            <button type="button" className="mudanza-btn primary" onClick={handleSubmit} disabled={saving}>
                                {saving ? 'Enviando…' : 'Enviar solicitud'}
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
};

SolicitudMudanzaModal.propTypes = {
    fraccionamientoId: PropTypes.string,
    currentUser: PropTypes.shape({
        uid: PropTypes.string,
        displayName: PropTypes.string,
        email: PropTypes.string,
    }),
    userData: PropTypes.shape({
        rol: PropTypes.string,
        nombreCompleto: PropTypes.string,
        nombre: PropTypes.string,
        segundoNombre: PropTypes.string,
        apellidoPaterno: PropTypes.string,
        apellidoMaterno: PropTypes.string,
        apellidos: PropTypes.string,
        telefono: PropTypes.string,
        celular: PropTypes.string,
        telefonoPrincipal: PropTypes.string,
        email: PropTypes.string,
        lote: PropTypes.string,
        numeroLote: PropTypes.string,
        houseLabel: PropTypes.string,
        displayName: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

export default SolicitudMudanzaModal;
